/**
 * Quiz History page - displays history of quiz attempts for a specific quiz
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QuizHistory as QuizHistoryType } from '../db/schema';
import './history.css';

interface Quiz {
    id: number;
    title: string;
    description: string;
}

interface QuizHistoryWithQuiz extends QuizHistoryType {
    quizTitle?: string;
}

export default function QuizHistory() {
    const { id } = useParams<{ id: string }>();
    const quizId = Number(id);
    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [history, setHistory] = useState<QuizHistoryWithQuiz[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch quiz details
                const quizResponse = await fetch(`/api/quizzes/${quizId}`);
                if (quizResponse.ok) {
                    const quizData = await quizResponse.json();
                    setQuiz(quizData.quiz);
                }

                // Fetch all history for the current user
                const historyResponse = await fetch('/api/quiz-history');
                if (historyResponse.ok) {
                    const historyData = await historyResponse.json();
                    // Filter history for this specific quiz
                    const quizHistory = historyData.history.filter(
                        (h: QuizHistoryType) => h.quizId === quizId
                    );
                    setHistory(quizHistory);
                }
            } catch (error) {
                console.error('Failed to fetch quiz history:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [quizId]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <main className="quizHistoryMain">
                <div className="quizHistoryHeader">
                    <Link to="/" className="backLink">
                        ← Back to Quizzes
                    </Link>
                </div>
                <div className="loadingMessage">Loading history...</div>
            </main>
        );
    }

    return (
        <main className="quizHistoryMain">
            <div className="quizHistoryHeader">
                <Link to="/" className="backLink">
                    ← Back to Quizzes
                </Link>
            </div>

            {quiz && (
                <div className="quizHistoryTitle">
                    <h1>Quiz History: {quiz.title}</h1>
                    <p className="quizDescription">{quiz.description}</p>
                </div>
            )}

            {history.length === 0 ? (
                <div className="noHistoryMessage">
                    <p>No quiz history found for this quiz.</p>
                    <p className="noHistorySubtext">
                        Take this quiz to start building your history!
                    </p>
                    <Link to={`/quiz/${quizId}/take`} className="takeQuizLink">
                        Take Quiz Now
                    </Link>
                </div>
            ) : (
                <div className="historyList">
                    <h2>Previous Attempts</h2>
                    {history.map((entry) => (
                        <div key={entry.id} className="historyItem">
                            <div className="historyItemHeader">
                                <div className="historyItemDate">
                                    {formatDate(entry.dateTaken)}
                                </div>
                                {entry.score !== null && (
                                    <div className="historyItemScore">
                                        Score: {entry.score}%
                                    </div>
                                )}
                            </div>
                            {entry.dateCompleted ? (
                                <div className="historyItemStatus completed">
                                    Completed
                                </div>
                            ) : (
                                <div className="historyItemStatus incomplete">
                                    Incomplete
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
}
